import axios from "axios";

import type { GetNoteImagePresignedUrlBodyContentType } from "~/api/model/getNoteImagePresignedUrlBodyContentType";
import type { GetNoteImagePresignedUrlBodyExt } from "~/api/model/getNoteImagePresignedUrlBodyExt";
import { getNoteImagePresignedUrl } from "~/api/notes/notes";
import logger from "~/utils/logger";

const MIME_TO_EXT: Record<string, GetNoteImagePresignedUrlBodyExt> = {
  "image/png": "png",
  "image/jpeg": "jpeg",
  "image/gif": "gif",
};

const SUPPORTED_CONTENT_TYPES = new Set<string>([
  "image/png",
  "image/jpeg",
  "image/gif",
]);

/**
 * Convert a base64 data URL to a Blob.
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64Data] = dataUrl.split(",");
  const mimeMatch = header.match(/data:(.*?);/);
  const mime = mimeMatch?.[1] ?? "application/octet-stream";
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

/**
 * Extract content type from a base64 data URL.
 */
function getContentTypeFromDataUrl(
  dataUrl: string
): GetNoteImagePresignedUrlBodyContentType | null {
  const match = dataUrl.match(/^data:(image\/(?:png|jpeg|gif));base64,/);
  return match ? (match[1] as GetNoteImagePresignedUrlBodyContentType) : null;
}

/**
 * Upload a single image  to S3 via presigned URL
 */
export async function uploadNoteImage(
  organizationId: string,
  projectId: string,
  source: File | string
): Promise<string> {
  let contentType: GetNoteImagePresignedUrlBodyContentType;
  let ext: GetNoteImagePresignedUrlBodyExt;
  let blob: Blob;

  if (typeof source === "string") {
    const ct = getContentTypeFromDataUrl(source);
    if (!ct) throw new Error("Unsupported image content type in data URL");
    contentType = ct;
    ext = MIME_TO_EXT[contentType];
    blob = dataUrlToBlob(source);
  } else {
    if (!SUPPORTED_CONTENT_TYPES.has(source.type)) {
      throw new Error(`Unsupported image type: ${source.type}`);
    }
    contentType = source.type as GetNoteImagePresignedUrlBodyContentType;
    ext = MIME_TO_EXT[contentType];
    blob = source;
  }

  const { presignedUrl, imageUrl } = await getNoteImagePresignedUrl(
    organizationId,
    projectId,
    { contentType, ext }
  );

  await axios.put(presignedUrl, blob, {
    headers: { "Content-Type": contentType },
  });

  return imageUrl;
}

const BASE64_IMG_REGEX =
  /<img\s[^>]*src="(data:image\/(?:png|jpeg|gif);base64,[^"]+)"[^>]*>/gi;

/**
 * Find all base64-encoded images in HTML content, upload each to S3
 */
export async function replaceBase64ImagesWithCdn(
  html: string,
  organizationId: string,
  projectId: string
): Promise<string> {
  const matches: { fullMatch: string; dataUrl: string }[] = [];
  let match: RegExpExecArray | null;

  BASE64_IMG_REGEX.lastIndex = 0;
  while ((match = BASE64_IMG_REGEX.exec(html)) !== null) {
    matches.push({ fullMatch: match[0], dataUrl: match[1] });
  }

  if (matches.length === 0) return html;

  const uploadResults = await Promise.allSettled(
    matches.map(async ({ dataUrl }) => {
      const cdnUrl = await uploadNoteImage(organizationId, projectId, dataUrl);
      return { dataUrl, cdnUrl };
    })
  );

  let result = html;
  for (const uploadResult of uploadResults) {
    if (uploadResult.status === "fulfilled") {
      const { dataUrl, cdnUrl } = uploadResult.value;
      result = result.split(dataUrl).join(cdnUrl);
    } else {
      logger.error("Failed to upload note image:", uploadResult.reason);
    }
  }

  return result;
}

/**
 * Check if HTML content contains any base64-encoded images
 */
export function hasBase64Images(html: string): boolean {
  BASE64_IMG_REGEX.lastIndex = 0;
  return BASE64_IMG_REGEX.test(html);
}
